import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getApiUrl } from '../../config/api';

const RentSubmit = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [securityDeposit, setSecurityDeposit] = useState('');
  const [depositEdited, setDepositEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'vastra';
  const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dgfwqjq8p';

  // Move useEffect to the top level
  useEffect(() => {
    if (!depositEdited && price) {
      setSecurityDeposit(price ? String(Number(price) * 2) : '');
    }
  }, [price]);

  if (!user) {
    return <div className="max-w-xl mx-auto mt-16 text-center text-pink-600 font-semibold text-lg">Please log in to submit a rental item.</div>;
  }

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    const videoFiles = files.filter(f => f.type.startsWith('video/'));
    setImages(prev => [...prev, ...imageFiles]);
    setVideos(prev => [...prev, ...videoFiles]);
  };

  const handleRemoveImage = (idx) => {
    setImages(images => images.filter((_, i) => i !== idx));
  };
  const handleRemoveVideo = (idx) => {
    setVideos(videos => videos.filter((_, i) => i !== idx));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    const videoFiles = files.filter(f => f.type.startsWith('video/'));
    setImages(prev => [...prev, ...imageFiles]);
    setVideos(prev => [...prev, ...videoFiles]);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Upload a file to Cloudinary and return the URL
  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    const res = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`,
      { method: 'POST', body: formData }
    );
    const data = await res.json();
    return data.secure_url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');
    setUploading(true);
    // Frontend validation for required fields
    if (!title.trim() || !description.trim() || !price) {
      setError('Please fill in all required fields (Title, Description, Price).');
      setSubmitting(false);
      setUploading(false);
      return;
    }
    try {
      // Upload all images and videos to Cloudinary first
      const imageUploadPromises = images.map(uploadToCloudinary);
      const videoUploadPromises = videos.map(uploadToCloudinary);
      const imageUrls = await Promise.all(imageUploadPromises);
      const videoUrls = await Promise.all(videoUploadPromises);
      setUploading(false);
      // Now submit only the URLs to the backend
      const formData = new FormData();
      formData.append('name', title); // Map title to name
      formData.append('description', description);
      formData.append('price', price);
      imageUrls.forEach((url) => formData.append('rentalImages', url));
      videoUrls.forEach((url) => formData.append('rentalVideos', url));
      formData.append('isRental', 'true');
      formData.append('securityDeposit', securityDeposit);
      const res = await fetch(getApiUrl('/api/products/rentals/submit'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to submit rental item');
      }
      setSuccess('Rental item submitted for review!');
      setTitle('');
      setDescription('');
      setPrice('');
      setImages([]);
      setVideos([]);
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setError(err.message);
      setUploading(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl w-full mx-auto mt-16 bg-white p-8 rounded-2xl shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-center text-pink-700">Rent Out Your Outfit — Make Style Earn!</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full">
        <input
          type="text"
          className="border rounded-lg px-4 py-2"
          placeholder="Title (e.g. Bridal Lehenga)"
          value={title}
          onChange={e => setTitle(e.target.value)}
          required
        />
        <textarea
          className="border rounded-lg px-4 py-2"
          placeholder="Description"
          rows={4}
          value={description}
          onChange={e => setDescription(e.target.value)}
          required
        />
        <input
          type="number"
          className="border rounded-lg px-4 py-2"
          placeholder="Rental Price (₹)"
          value={price}
          onChange={e => setPrice(e.target.value)}
          required
        />
        <input
          type="number"
          className="border rounded-lg px-4 py-2"
          placeholder={`Security Deposit (₹) e.g. ${price ? Number(price) * 2 : ''}`}
          value={securityDeposit}
          onChange={e => {
            setSecurityDeposit(e.target.value);
            setDepositEdited(true);
          }}
          min={0}
          required
        />
        <label className="font-semibold">Upload Images (Front, Back, Full):</label>
        <div
          className="border-2 border-dashed border-pink-300 rounded-lg p-4 mb-2 flex flex-col items-center justify-center cursor-pointer bg-pink-50 hover:bg-pink-100 transition w-full"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileChange}
            className="hidden"
            id="rental-image-upload"
            required={images.length === 0 && videos.length === 0}
          />
          <label htmlFor="rental-image-upload" className="cursor-pointer text-pink-600 font-semibold w-full text-center">
            Click or drag images/videos here to upload
          </label>
        </div>
        <div className="flex gap-2 mt-2 flex-wrap w-full overflow-x-auto">
          {images && images.length > 0 && images.map((img, idx) => (
            <div key={idx} className="relative group">
              <img
                src={URL.createObjectURL(img)}
                alt={`preview-img-${idx}`}
                className="w-20 h-20 object-cover rounded-lg border"
              />
              <button
                type="button"
                className="absolute top-1 right-1 bg-white bg-opacity-80 rounded-full p-1 text-pink-600 hover:bg-pink-100 group-hover:opacity-100 opacity-70 transition"
                onClick={() => handleRemoveImage(idx)}
                aria-label="Remove image"
              >
                &times;
              </button>
            </div>
          ))}
          {videos && videos.length > 0 && videos.map((vid, idx) => (
            <div key={idx} className="relative group">
              <video
                src={URL.createObjectURL(vid)}
                controls
                className="w-24 h-20 object-cover rounded-lg border"
              />
              <button
                type="button"
                className="absolute top-1 right-1 bg-white bg-opacity-80 rounded-full p-1 text-pink-600 hover:bg-pink-100 group-hover:opacity-100 opacity-70 transition"
                onClick={() => handleRemoveVideo(idx)}
                aria-label="Remove video"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
        <button
          type="submit"
          className="bg-pink-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-pink-700 transition mt-4 disabled:opacity-50 w-full"
          disabled={submitting || (images.length === 0 && videos.length === 0) || uploading}
        >
          {uploading ? 'Uploading...' : submitting ? 'Submitting...' : 'Submit for Review'}
        </button>
        {error && <div className="text-red-600 text-sm mt-2">{error}</div>}
        {success && <div className="text-green-600 text-sm mt-2">{success}</div>}
      </form>
    </div>
  );
};

export default RentSubmit; 